import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'

import { makeStyles } from 'tss-react/mui'

import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import SearchIcon from '@mui/icons-material/Search'
import TuneIcon from '@mui/icons-material/Tune'

import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'

import { DateFns } from 'src/utils/date-fns'
import { formatNumber, parseLocalDateNoTZ, parseLocalDateTimeNoTZ } from 'src/utils/formatter-utils'
import {
    ActionsRowResponsive,
    buttonSx,
    chipSx,
    classToFComponent,
    dataGridRowHoverSx,
    dataGridSx,
    EditorBody,
    EditorSideColumn,
    FCClassContext,
    Field,
    FieldHelp,
    FieldLabel,
    inputSx,
    LifesHubChipTone,
    LifesHubConfirmDialog,
    LifesHubEmptyState,
    PanelCard,
    PanelCardBody,
    SkeletonBlock,
    SwitchRow,
    switchSx
} from 'src/utils/views'

import {
    EditableNotificationFields,
    NotificationStyle,
    NotificationTabScope,
    NotificationUserRow,
    ShowNotificationRow
} from '../scopes'
import ManageTypeModal from './manage_type_modal'
import { Banner, CardHead, FieldCaption, FieldGrid, SettingsStack } from 'src/utils/views/modules/admin-configurations'

const LOG = Logger.get('NotificationManager-VIEW')

const StyleMapper: Record<NotificationStyle, string> = {
    [NotificationStyle.INFO]: 'Informação',
    [NotificationStyle.SUCCESS]: 'Sucesso',
    [NotificationStyle.WARNING]: 'Aviso',
    [NotificationStyle.ERROR]: 'Erro'
}

const StyleToneMapper: Record<NotificationStyle, LifesHubChipTone> = {
    [NotificationStyle.INFO]: 'blue',
    [NotificationStyle.SUCCESS]: 'green',
    [NotificationStyle.WARNING]: 'orange',
    [NotificationStyle.ERROR]: 'red'
}

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0,
        flexGrow: 1
    },
    input: {
        ...inputSx
    },
    filterRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 2.1fr) auto',
        gap: 12,
        alignItems: 'end',
        '@media (max-width: 1050px)': {
            gridTemplateColumns: 'minmax(0, 1fr)'
        }
    },
    filterActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        '@media (max-width: 720px)': {
            '& .MuiButton-root': { flexGrow: 1 }
        }
    },
    resultsCount: {
        marginTop: 15,
        color: 'var(--green-800)',
        fontWeight: 700
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
        padding: 18
    },
    grid: {
        ...dataGridSx,
        ...dataGridRowHoverSx,
        minHeight: 380,
        '& .MuiDataGrid-cell.actions': {
            padding: '0 6px'
        }
    },
    usersGrid: {
        ...dataGridSx,
        ...dataGridRowHoverSx,
        minHeight: 260,
        '& .MuiDataGrid-cell.actions': {
            padding: '0 6px'
        }
    },
    switchStack: {
        display: 'grid',
        gap: 14
    },
    chips: {
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        flexWrap: 'wrap'
    }
})

type NotificationManagerViewProps = {
    className?: string
    scope: NotificationTabScope
}

class NotificationManagerClass implements FCClassContext<NotificationManagerViewProps> {
    // :: emissors
    readonly emitFind = () => {
        const nameFilter = this.nameFilterInputRef.current?.value || ''
        this.scope.onFind(nameFilter).catch(LOG.caught)
    }

    readonly emitNewClick = () => {
        this.scope.onNew().catch(LOG.caught)
    }

    readonly emitEditClick = (id: GridRowId) => () => {
        this.scope.onEdit(Number(id)).catch(LOG.caught)
    }

    readonly emitDeleteClick = (id: GridRowId) => () => {
        this.scope.onDelete(Number(id)).catch(LOG.caught)
    }

    readonly emitDeleteUserClick = (id: GridRowId) => () => {
        this.scope.onDeleteUser(Number(id)).catch(LOG.caught)
    }

    readonly emitOnYesResponse = () => {
        this.scope.onYesNoResponse(true).catch(LOG.caught)
    }

    readonly emitOnNoResponse = () => {
        this.scope.onYesNoResponse(false).catch(LOG.caught)
    }

    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    // :: Fields
    scope!: NotificationTabScope
    sytles!: ReturnType<typeof useStyles>
    columns = static_build_columns(this.emitEditClick, this.emitDeleteClick)
    usersColumns = static_build_users_columns(this.emitDeleteUserClick)
    nameFilterInputRef!: React.RefObject<HTMLInputElement | null>

    // :: Methods

    onSyncState({ scope }: NotificationManagerViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    onFieldValueChanged<K extends keyof EditableNotificationFields>(field: K, value: NotificationTabScope[K]) {
        this.scope.onFieldChanged(field, value).catch(LOG.caught)
    }

    onGroupsChanged(event: React.SyntheticEvent, newValue: (number | undefined)[]) {
        event.preventDefault()
        this.scope.onGroupsChanged(newValue).catch(LOG.caught)
    }

    onUsersChanged(event: React.SyntheticEvent, newValue: number | null) {
        event.preventDefault()
        this.scope.onUsersChanged(newValue).catch(LOG.caught)
    }

    onAddSelectedUser() {
        this.scope.onAddSelectedUser().catch(LOG.caught)
    }

    onManageTypesOpen() {
        this.scope.manageTypeScope.onOpen().catch(LOG.caught)
    }

    render({ className, scope }: NotificationManagerViewProps) {
        this.nameFilterInputRef = React.useRef<HTMLInputElement>(null)

        const classes = this.sytles.classes

        if (scope.waiting) {
            return (
                <div className={clsx(classes.view, className)}>
                    <SkeletonBlock sx={{ height: 118 }} />
                    <SkeletonBlock sx={{ height: 420 }} />
                </div>
            )
        }

        return (
            <div className={clsx(classes.view, className)}>
                {scope.tab === 'form' ? this.renderForm(scope) : this.renderList(scope)}

                <LifesHubConfirmDialog
                    open={!!scope.askYesOrNo}
                    title="Confirmar exclusão"
                    tone="danger"
                    message={scope.askYesOrNo}
                    confirmLabel="Excluir"
                    footerNote="A remoção é imediata e não pode ser desfeita por aqui."
                    onConfirm={this.emitOnYesResponse}
                    onCancel={this.emitOnNoResponse}
                />
                <ManageTypeModal scope={scope.manageTypeScope} />
            </div>
        )
    }

    renderList(scope: NotificationTabScope) {
        const { classes } = this.sytles

        return (
            <>
                <PanelCard elevation={0} sx={{ padding: '18px', flexShrink: 0 }}>
                    <div className={classes.filterRow}>
                        <Field>
                            <FieldLabel htmlFor="notificationFilter">Buscar</FieldLabel>
                            <TextField
                                id="notificationFilter"
                                className={classes.input}
                                fullWidth
                                defaultValue={scope.filter}
                                inputRef={this.nameFilterInputRef}
                                placeholder="Buscar pelo título da notificação"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: 20, color: 'var(--muted)' }} />
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            />
                        </Field>
                        <div className={classes.filterActions}>
                            <Button sx={buttonSx()} startIcon={<SearchIcon />} onClick={this.emitFind}>
                                Buscar
                            </Button>
                            {this.renderNewButton()}
                        </div>
                    </div>
                    <div className={classes.resultsCount}>
                        {pluralize(scope.notifications.length, 'notificação encontrada', 'notificações encontradas')}
                    </div>
                </PanelCard>

                <PanelCard elevation={0} sx={{ flexGrow: 1, minHeight: 420 }}>
                    {scope.notifications.length === 0 ? (
                        <LifesHubEmptyState
                            title="Nenhuma notificação encontrada"
                            text={
                                scope.filter
                                    ? 'Nenhuma notificação corresponde ao texto buscado. Limpe a busca para ver a lista completa.'
                                    : 'Publique um aviso para que ele apareça no sino de notificações dos usuários.'
                            }
                            actions={this.renderNewButton()}
                        />
                    ) : (
                        <div className={classes.cardBody}>
                            <DataGrid
                                className={classes.grid}
                                rows={scope.notifications}
                                columns={this.columns}
                                getRowId={static_getRowId}
                                disableRowSelectionOnClick
                                density="compact"
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10
                                        }
                                    }
                                }}
                                pageSizeOptions={[10, 25, 50, 100]}
                            />
                        </div>
                    )}
                </PanelCard>
            </>
        )
    }

    renderNewButton() {
        return (
            <Button sx={buttonSx('brand')} startIcon={<AddIcon />} onClick={this.emitNewClick}>
                Nova notificação
            </Button>
        )
    }

    renderForm(scope: NotificationTabScope) {
        const isNew = !scope.notificationId

        return (
            <EditorBody>
                <SettingsStack>
                    {this.renderFormHeaderCard(isNew)}
                    {this.renderContentCard(scope)}
                    {this.renderPublicationCard(scope)}
                    {!scope.toAllUsers && this.renderAudienceCard(scope)}
                    {this.renderRecipientsCard(scope)}
                </SettingsStack>
                <EditorSideColumn>{this.renderSummary(scope, isNew)}</EditorSideColumn>
            </EditorBody>
        )
    }

    renderFormHeaderCard(isNew: boolean) {
        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>{isNew ? 'Nova notificação' : 'Editando notificação'}</h3>
                        <p className="subtitle">
                            A notificação aparece no sino dos usuários alcançados enquanto estiver ativa e não expirada.
                        </p>
                    </div>
                    <ActionsRowResponsive>
                        <Button sx={buttonSx()} startIcon={<ArrowBackIcon />} onClick={this.emitOnClose}>
                            Voltar
                        </Button>
                        <Button sx={buttonSx('primary')} startIcon={<SaveOutlinedIcon />} onClick={this.emitOnSave}>
                            Salvar notificação
                        </Button>
                    </ActionsRowResponsive>
                </CardHead>
            </PanelCard>
        )
    }

    renderContentCard(scope: NotificationTabScope) {
        const { classes } = this.sytles

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Conteúdo</h3>
                        <p className="subtitle">O que o usuário lê no sino e ao abrir a notificação.</p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    <Field>
                        <FieldLabel htmlFor="notificationTitle">Título</FieldLabel>
                        <TextField
                            id="notificationTitle"
                            className={classes.input}
                            fullWidth
                            defaultValue={scope.title}
                            onChange={(e) => this.onFieldValueChanged('title', e.target.value)}
                        />
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="notificationSummary">Sumário</FieldLabel>
                        <TextField
                            id="notificationSummary"
                            className={classes.input}
                            fullWidth
                            defaultValue={scope.summary}
                            onChange={(e) => this.onFieldValueChanged('summary', e.target.value)}
                        />
                        <FieldHelp>
                            É a linha que aparece na lista do sino, antes de a notificação ser aberta.
                        </FieldHelp>
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="notificationContent">Conteúdo</FieldLabel>
                        <TextField
                            id="notificationContent"
                            className={classes.input}
                            fullWidth
                            multiline
                            rows={4}
                            defaultValue={scope.content}
                            onChange={(e) => this.onFieldValueChanged('content', e.target.value)}
                        />
                    </Field>

                    <FieldGrid>
                        {this.renderNoticationType(scope)}
                        <Field>
                            <FieldLabel htmlFor="notificationStyle">Estilo</FieldLabel>
                            <TextField
                                id="notificationStyle"
                                className={classes.input}
                                select
                                fullWidth
                                defaultValue={scope.style}
                                onChange={(e) => this.onFieldValueChanged('style', e.target.value as NotificationStyle)}
                            >
                                {Object.values(NotificationStyle).map((style) => (
                                    <MenuItem key={style} value={style}>
                                        <Chip
                                            size="small"
                                            label={StyleMapper[style]}
                                            sx={chipSx(StyleToneMapper[style])}
                                        />
                                    </MenuItem>
                                ))}
                            </TextField>
                            <FieldHelp>Define a cor com que o aviso é destacado para o usuário.</FieldHelp>
                        </Field>
                    </FieldGrid>
                </PanelCardBody>
            </PanelCard>
        )
    }

    renderNoticationType(scope: NotificationTabScope) {
        const { classes } = this.sytles

        return (
            <Field>
                <FieldLabel htmlFor="notificationType">Tipo</FieldLabel>
                <Autocomplete
                    id="notificationType"
                    options={scope.allNotificationTypes.map((t) => t.id) || []}
                    getOptionDisabled={(notifId) => !scope.allNotificationTypes?.find((t) => t.id === notifId)?.active}
                    value={scope.typeId || null}
                    getOptionLabel={(id) => scope.allNotificationTypes?.find((t) => t.id === id)?.name || String(id)}
                    onChange={(_, newId) => this.onFieldValueChanged('typeId', newId)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            className={classes.input}
                            fullWidth
                            placeholder="Selecione"
                            slotProps={{
                                ...params.slotProps,

                                input: {
                                    ...params.slotProps.input,
                                    endAdornment: (
                                        <>
                                            <InputAdornment position="end">
                                                <Tooltip title="Gerenciar tipos">
                                                    <Button
                                                        sx={buttonSx('tertiary', true)}
                                                        onClick={() => this.onManageTypesOpen()}
                                                        startIcon={<TuneIcon />}
                                                        aria-label="Gerenciar tipos"
                                                    >
                                                        Tipos
                                                    </Button>
                                                </Tooltip>
                                            </InputAdornment>
                                            {params.slotProps.input.endAdornment}
                                        </>
                                    )
                                }
                            }}
                        />
                    )}
                />
                <FieldHelp>Agrupa as notificações para o usuário filtrar no sino.</FieldHelp>
            </Field>
        )
    }

    renderPublicationCard(scope: NotificationTabScope) {
        const { classes } = this.sytles

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Publicação</h3>
                        <p className="subtitle">Quando o aviso vale e como ele se comporta na tela do usuário.</p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    <FieldGrid>
                        <Field>
                            <FieldLabel htmlFor="notificationDate">Data da notificação</FieldLabel>
                            <DatePicker
                                formatDensity="dense"
                                defaultValue={scope.notificationDate ? dayjs(scope.notificationDate) : null}
                                onChange={(newVal) =>
                                    this.onFieldValueChanged(
                                        'notificationDate',
                                        newVal ? new Date(newVal.toISOString()) : undefined
                                    )
                                }
                                slotProps={{
                                    textField: { id: 'notificationDate', className: classes.input, fullWidth: true }
                                }}
                            />
                            <FieldHelp>Data mostrada ao usuário como referência do aviso.</FieldHelp>
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="notificationExpiresIn">Expira em</FieldLabel>
                            <DatePicker
                                formatDensity="dense"
                                defaultValue={scope.expiresIn ? dayjs(scope.expiresIn) : null}
                                onChange={(newVal) =>
                                    this.onFieldValueChanged(
                                        'expiresIn',
                                        newVal ? new Date(newVal.toISOString()) : undefined
                                    )
                                }
                                slotProps={{
                                    textField: {
                                        id: 'notificationExpiresIn',
                                        className: classes.input,
                                        fullWidth: true
                                    }
                                }}
                            />
                            <FieldHelp>Sem data, a notificação continua visível por tempo indeterminado.</FieldHelp>
                        </Field>
                    </FieldGrid>

                    <div className={classes.switchStack}>
                        <SwitchRow>
                            <Switch
                                sx={switchSx}
                                size="small"
                                checked={scope.active || false}
                                onChange={(e) => this.onFieldValueChanged('active', e.target.checked)}
                                slotProps={{
                                    input: { 'aria-label': 'Notificação ativa' }
                                }}
                            />
                            <div>
                                <b>Notificação ativa</b>
                                <FieldHelp>Enquanto estiver desligada, ninguém recebe o aviso.</FieldHelp>
                            </div>
                        </SwitchRow>
                        <SwitchRow>
                            <Switch
                                sx={switchSx}
                                size="small"
                                checked={scope.alert || false}
                                onChange={(e) => this.onFieldValueChanged('alert', e.target.checked)}
                                slotProps={{
                                    input: { 'aria-label': 'Exibir como alerta' }
                                }}
                            />
                            <div>
                                <b>Exibir como alerta</b>
                                <FieldHelp>O aviso é aberto na frente do usuário, em vez de esperar no sino.</FieldHelp>
                            </div>
                        </SwitchRow>
                        <SwitchRow>
                            <Switch
                                sx={switchSx}
                                size="small"
                                checked={scope.canMarkAsRead || false}
                                onChange={(e) => this.onFieldValueChanged('canMarkAsRead', e.target.checked)}
                                slotProps={{
                                    input: { 'aria-label': 'Permitir marcar como lida' }
                                }}
                            />
                            <div>
                                <b>Permitir marcar como lida</b>
                                <FieldHelp>Sem isso, o aviso continua aparecendo até expirar.</FieldHelp>
                            </div>
                        </SwitchRow>
                        <SwitchRow>
                            <Switch
                                sx={switchSx}
                                size="small"
                                checked={scope.toAllUsers || false}
                                onChange={(e) => this.onFieldValueChanged('toAllUsers', e.target.checked)}
                                slotProps={{
                                    input: { 'aria-label': 'Enviar para todos os usuários' }
                                }}
                            />
                            <div>
                                <b>Enviar para todos os usuários</b>
                                <FieldHelp>
                                    Desligue para escolher grupos e usuários específicos como destinatários.
                                </FieldHelp>
                            </div>
                        </SwitchRow>
                    </div>
                </PanelCardBody>
            </PanelCard>
        )
    }

    renderAudienceCard(scope: NotificationTabScope) {
        const { classes } = this.sytles

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Público</h3>
                        <p className="subtitle">Quem passa a enxergar a notificação no sino.</p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    <Field>
                        <FieldLabel htmlFor="notificationGroups">Grupos</FieldLabel>
                        <Autocomplete
                            id="notificationGroups"
                            multiple
                            options={scope.allGroups?.map((g) => g.id) || []}
                            value={scope.groups?.map((g) => g.groupId) || []}
                            getOptionLabel={(id) => scope.allGroups?.find((g) => g.id === id)?.name || String(id)}
                            disableCloseOnSelect
                            onChange={(e, newValue) => this.onGroupsChanged(e, newValue)}
                            renderValue={(value, getItemProps) =>
                                value.map((id, index) => (
                                    <Chip
                                        size="small"
                                        label={scope.allGroups?.find((g) => g.id === id)?.name || String(id)}
                                        sx={chipSx('blue')}
                                        {...getItemProps({ index })}
                                        key={id}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} className={classes.input} placeholder="Selecione os grupos" />
                            )}
                        />
                        <FieldHelp>Todos os usuários dos grupos escolhidos entram na lista de destinatários.</FieldHelp>
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="notificationUser">Usuário avulso</FieldLabel>
                        <Autocomplete
                            id="notificationUser"
                            options={scope.allUsers.map((g) => g.id) || []}
                            getOptionDisabled={(userId) => scope.users?.some((usr) => usr.userId === userId)}
                            value={scope.selectedUserId}
                            getOptionLabel={(id) => scope.allUsers?.find((g) => g.id === id)?.name || String(id)}
                            onChange={(e, newValue) => this.onUsersChanged(e, newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    className={classes.input}
                                    placeholder="Selecione um usuário"
                                    slotProps={{
                                        ...params.slotProps,

                                        input: {
                                            ...params.slotProps.input,
                                            endAdornment: (
                                                <>
                                                    <InputAdornment position="end">
                                                        <Button
                                                            sx={buttonSx('tertiary', true)}
                                                            disabled={!scope.selectedUserId}
                                                            onClick={() => this.onAddSelectedUser()}
                                                            startIcon={<PersonAddAltIcon />}
                                                        >
                                                            Adicionar
                                                        </Button>
                                                    </InputAdornment>
                                                    {params.slotProps.input.endAdornment}
                                                </>
                                            )
                                        }
                                    }}
                                />
                            )}
                        />
                        <FieldHelp>Inclui uma pessoa que não está em nenhum dos grupos escolhidos.</FieldHelp>
                    </Field>
                </PanelCardBody>
            </PanelCard>
        )
    }

    renderRecipientsCard(scope: NotificationTabScope) {
        const { classes } = this.sytles

        const users = scope.users || []

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Destinatários</h3>
                        <p className="subtitle">Quem já está na lista e quando cada um leu o aviso.</p>
                    </div>
                </CardHead>
                {users.length === 0 ? (
                    <LifesHubEmptyState
                        title="Nenhum destinatário individual"
                        text={
                            scope.toAllUsers
                                ? 'A notificação vai para todos os usuários — a lista individual só é montada quando o envio é restrito.'
                                : 'Escolha grupos ou adicione um usuário avulso para montar a lista de destinatários.'
                        }
                    />
                ) : (
                    <div className={classes.cardBody}>
                        <DataGrid
                            className={classes.usersGrid}
                            rows={users}
                            columns={this.usersColumns}
                            getRowId={static_getUsersRowId}
                            disableRowSelectionOnClick
                            density="compact"
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 15
                                    }
                                }
                            }}
                            pageSizeOptions={[15, 25, 50, 100]}
                        />
                    </div>
                )}
            </PanelCard>
        )
    }

    renderSummary(scope: NotificationTabScope, isNew: boolean) {
        const { classes } = this.sytles

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Resumo</h3>
                        <p className="subtitle">Como o aviso vai sair.</p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    {isNew && <Banner tone="blue">A notificação só chega aos usuários depois de salva.</Banner>}

                    <Field>
                        <FieldCaption>Situação</FieldCaption>
                        <div className={classes.chips}>
                            <Chip
                                size="small"
                                label={scope.active ? 'Ativa' : 'Inativa'}
                                sx={chipSx(scope.active ? 'green' : 'gray')}
                            />
                            <Chip
                                size="small"
                                label={StyleMapper[scope.style]}
                                sx={chipSx(StyleToneMapper[scope.style])}
                            />
                            {scope.alert && <Chip size="small" label="Alerta" sx={chipSx('orange')} />}
                            {!scope.canMarkAsRead && <Chip size="small" label="Não dispensável" sx={chipSx('gray')} />}
                        </div>
                    </Field>

                    <Field>
                        <FieldCaption>Alcance</FieldCaption>
                        <div className={classes.chips}>
                            {scope.toAllUsers ? (
                                <Chip size="small" label="Todos os usuários" sx={chipSx('blue')} />
                            ) : (
                                <>
                                    <Chip
                                        size="small"
                                        label={pluralize(scope.groups?.length ?? 0, 'grupo', 'grupos')}
                                        sx={chipSx('blue')}
                                    />
                                    <Chip
                                        size="small"
                                        label={pluralize(scope.users?.length ?? 0, 'usuário', 'usuários')}
                                        sx={chipSx('gray')}
                                    />
                                </>
                            )}
                        </div>
                    </Field>

                    <Field>
                        <FieldCaption>Validade</FieldCaption>
                        <FieldHelp>
                            {scope.expiresIn
                                ? `Expira em ${DateFns.formatDate(scope.expiresIn, 'dd/MM/yyyy')}.`
                                : 'Sem data de expiração.'}
                        </FieldHelp>
                    </Field>
                </PanelCardBody>
            </PanelCard>
        )
    }
}

export const NotificationManagerView = classToFComponent(NotificationManagerClass, React)
export default NotificationManagerView

function pluralize(count: number, singular: string, plural: string): string {
    return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

function static_build_columns(
    handleEditClick: (id: GridRowId) => () => void,
    handleDeleteClick: (id: GridRowId) => () => void
): GridColDef[] {
    return [
        {
            field: 'title',
            headerName: 'Notificação',
            width: 260,
            renderCell({ value, row }) {
                const label = (value as string) ?? '—'
                const summary = (row.summary as string) ?? ''

                return (
                    <div style={{ minWidth: 0, paddingTop: 2, paddingBottom: 2 }}>
                        <div style={{ fontWeight: 750, lineHeight: 1.25 }} title={label}>
                            {label}
                        </div>
                        <div
                            style={{
                                color: 'var(--muted)',
                                fontSize: 12,
                                lineHeight: 1.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                            title={summary}
                        >
                            {summary || '—'}
                        </div>
                    </div>
                )
            }
        },
        {
            field: 'notificationStyle',
            headerName: 'Estilo',
            width: 120,
            renderCell({ value }) {
                const style = value as NotificationStyle
                return <Chip size="small" label={StyleMapper[style]} sx={chipSx(StyleToneMapper[style])} />
            }
        },
        {
            field: 'type',
            headerName: 'Tipo',
            width: 150,
            renderCell({ value }) {
                return value ? (
                    <Chip size="small" label={value as string} sx={chipSx('purple')} />
                ) : (
                    <span style={{ color: 'var(--muted-2)' }}>—</span>
                )
            }
        },
        {
            field: 'createdUser',
            headerName: 'Criador',
            width: 150
        },
        {
            field: 'groupsCount',
            headerName: 'Grupos',
            align: 'center',
            headerAlign: 'center',
            width: 100,
            valueFormatter: (value, row) => (!row.toAllUsers ? value : 'Todos')
        },
        {
            field: 'usersCount',
            headerName: 'Usuários',
            align: 'center',
            headerAlign: 'center',
            width: 100,
            valueFormatter: (value, row) => (!row.toAllUsers ? value : 'Todos')
        },
        {
            field: 'canMarkAsRead',
            headerName: 'Marca como lida',
            align: 'center',
            headerAlign: 'center',
            width: 140,
            valueFormatter: (value) => (value ? 'Sim' : 'Não')
        },
        {
            field: 'active',
            headerName: 'Status',
            align: 'center',
            headerAlign: 'center',
            width: 110,
            renderCell(params) {
                return params.value ? (
                    <Chip size="small" label="Ativa" sx={chipSx('green')} />
                ) : (
                    <Chip size="small" label="Inativa" sx={chipSx('gray')} />
                )
            }
        },
        {
            field: 'alert',
            headerName: 'Alerta',
            align: 'center',
            headerAlign: 'center',
            width: 100,
            renderCell(params) {
                return params.value ? (
                    <Chip size="small" label="Alerta" sx={chipSx('orange')} />
                ) : (
                    <span style={{ color: 'var(--muted-2)' }}>—</span>
                )
            }
        },
        {
            field: 'expiresIn',
            headerName: 'Expira em',
            width: 130,
            valueFormatter: (value) => (value ? DateFns.formatDate(value, 'dd/MM/yyyy') : 'Nunca')
        },
        {
            field: 'notificationDate',
            headerName: 'Data da notificação',
            width: 160,
            valueFormatter: (value) => (value ? DateFns.formatDate(parseLocalDateNoTZ(value), 'dd/MM/yyyy') : '')
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 96,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'edit' + id}
                        icon={
                            <Tooltip title="Editar notificação">
                                <EditOutlinedIcon sx={{ fontSize: 20 }} />
                            </Tooltip>
                        }
                        label="Editar notificação"
                        onClick={handleEditClick(id)}
                        style={{ color: 'var(--green-800)' }}
                    />,
                    <GridActionsCellItem
                        key={'del' + id}
                        icon={
                            <Tooltip title="Excluir notificação">
                                <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                            </Tooltip>
                        }
                        label="Excluir notificação"
                        onClick={handleDeleteClick(id)}
                        style={{ color: 'var(--danger)' }}
                    />
                ]
            }
        }
    ]
}

function static_build_users_columns(handleDeleteClick: (id: GridRowId) => () => void): GridColDef[] {
    return [
        {
            field: 'userName',
            headerName: 'Usuário',
            width: 220,
            renderCell({ value }) {
                const label = (value as string) ?? '—'
                return (
                    <span style={{ fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>
                        {label}
                    </span>
                )
            }
        },
        {
            field: 'groups',
            headerName: 'Grupos',
            flex: 1,
            minWidth: 200
        },
        {
            field: 'readAt',
            headerName: 'Lida em',
            width: 200,
            renderCell({ value }) {
                return value ? (
                    <span>{DateFns.formatDate(parseLocalDateTimeNoTZ(value), 'dd/MM/yyyy HH:mm')}</span>
                ) : (
                    <Chip size="small" label="Não lida" sx={chipSx('gray')} />
                )
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 64,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'del' + id}
                        icon={
                            <Tooltip title="Remover destinatário">
                                <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                            </Tooltip>
                        }
                        label="Remover destinatário"
                        onClick={handleDeleteClick(id)}
                        style={{ color: 'var(--danger)' }}
                    />
                ]
            }
        }
    ]
}

function static_getRowId(row: ShowNotificationRow) {
    return `${row.id}`
}

function static_getUsersRowId(row: NotificationUserRow) {
    return `${row.userId}`
}
